/**
 * One closing month, upload by upload — the drill-down under the monthly
 * chart. First the uploads of the month as an ordered timeline (precise
 * time, entry file, who launched it, what it became; each opens its run),
 * then the accounts × uploads table: one column per upload that produced
 * figures, every cell that differs from the previous upload highlighted,
 * and a Δ column (last − first). Changed account lines come first and, once
 * two uploads can be compared, alone by default; the toggle shows them all.
 * The activity toggle keeps one activity (CEM, RMC, AGG) apart from the
 * others, so an account is tracked within its own activity.
 *
 * An upload that finished well but produced no figures — the P&L inputs tool
 * run by hand — is named for what it is, and the tool that turns its output
 * into figures is one click away, launched on that output.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TableCell,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { api, type FigureSource, type MonthRun } from "../lib/api";
import { useApiQuery } from "../hooks/useApiQuery";
import { useEditMode } from "../hooks/useCustomer";
import { useToast } from "../hooks/useToast";
import {
  awaitingNextTool,
  cellChanged,
  changeRows,
  fullAmount,
  periodTitle,
  signedAmount,
} from "../lib/monthly";
import { errorText } from "../lib/errors";
import { formatDateTimeSeconds } from "../lib/runs";
import { resolveText } from "../lib/tooltext";
import { DataTable } from "./ui/DataTable";
import { EmptyState } from "./ui/EmptyState";
import { ErrorAlert } from "./ui/ErrorAlert";
import { StateChip } from "./ui/StateChip";

/** The activity toggle's "every activity" choice. */
const ALL_ENTITIES = "ALL";

export function MonthChanges({
  year,
  period,
  onClose,
}: {
  year: number;
  period: number;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const toast = useToast();
  const edit = useEditMode();
  const [launching, setLaunching] = useState<string | null>(null);
  const [changedOnly, setChangedOnly] = useState(true);
  const [search, setSearch] = useState("");
  // one activity, or every activity side by side (null)
  const [entity, setEntity] = useState<string | null>(null);

  const query = useApiQuery(["stats", "monthly", "changes", year, period], (signal) =>
    api.monthlyChanges(year, period, signal),
  );
  const changes = query.data ?? null;

  // two uploads with figures are the least a comparison needs; with none
  // changed, hiding the unchanged rows would hide everything
  const comparable = (changes?.compared.length ?? 0) > 1;
  const canNarrow = comparable && (changes?.changed_count ?? 0) > 0;
  const rows = useMemo(
    () =>
      changes
        ? changeRows(changes.accounts, {
            changedOnly: changedOnly && canNarrow,
            search,
            entity,
          })
        : [],
    [changes, changedOnly, canNarrow, search, entity],
  );
  const changedShown = useMemo(
    () =>
      changes
        ? changeRows(changes.accounts, { changedOnly: true, entity }).length
        : 0,
    [changes, entity],
  );
  // the table's upload columns, in the order of the timeline numbering
  const compared = useMemo(() => {
    if (!changes) return [] as { run: MonthRun; n: number }[];
    return changes.compared.flatMap((jobId) => {
      const index = changes.runs.findIndex((r) => r.job_id === jobId);
      return index === -1 ? [] : [{ run: changes.runs[index], n: index + 1 }];
    });
  }, [changes]);

  // uploads that wait for the next tool before they count as figures
  const pendingNext = useMemo(() => (changes ? awaitingNextTool(changes.runs) : []), [changes]);
  const launch = async (id: string, tool: FigureSource, artifactId: string) => {
    setLaunching(id);
    try {
      const started = await api.runTool({ tool: tool.key, artifact_id: artifactId });
      toast(
        t("monthly.nextToolStarted", { tool: resolveText(tool.display_name, i18n.language) }),
        "success",
      );
      navigate(`/runs/${started.job_id}`);
    } catch (err) {
      toast(errorText(err), "error");
    } finally {
      setLaunching(null);
    }
  };
  const runNext = (run: MonthRun) => {
    if (run.next_tool && run.output_artifact_id) {
      void launch(run.job_id, run.next_tool, run.output_artifact_id);
    }
  };
  // the month's figures stop short of the pipeline's end: the next tool,
  // launched on the latest upload's output, refines them
  const monthNext = changes?.next_tool ?? null;
  const monthNextInput = changes?.next_tool_input_artifact_id ?? null;

  const title = periodTitle(year, period, i18n.language);
  const changedBg = alpha(theme.palette.warning.main, theme.palette.mode === "dark" ? 0.28 : 0.18);

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6">{t("monthly.changesTitle", { month: title })}</Typography>
            {changes && (
              <Typography variant="caption" color="text.secondary">
                {t("monthly.changesSubtitle", {
                  uploads: t("monthly.uploads", { count: changes.runs.length }),
                  compared: changes.compared.length,
                  changed: changedShown,
                })}
                {changes.figures_from &&
                  ` · ${t("monthly.figuresSource", {
                    tool:
                      resolveText(changes.figures_from.display_name, i18n.language) ||
                      changes.figures_from.name,
                  })}`}
              </Typography>
            )}
          </Box>
          <Tooltip title={t("monthly.close")}>
            <IconButton size="small" aria-label={t("monthly.close")} onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        {!changes && query.isError && (
          <ErrorAlert error={query.error} onRetry={() => void query.refetch()} />
        )}
        {!changes && !query.isError && <LinearProgress aria-label={t("monthly.changesLoading")} />}

        {changes && changes.runs.length === 0 && <EmptyState message={t("monthly.noUploads")} />}

        {changes && changes.runs.length > 0 && (
          <>
            <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
              {t("monthly.timeline")}
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              sx={{ overflowX: "auto", pb: 1, mb: 2 }}
            >
              {changes.runs.map((run, index) => (
                <UploadCard key={run.job_id} run={run} n={index + 1} />
              ))}
            </Stack>

            {pendingNext.map((run) => {
              const next = run.next_tool!;
              const nextName = resolveText(next.display_name, i18n.language) || next.name;
              return (
                <Alert
                  key={run.job_id}
                  severity="info"
                  sx={{ mb: 1 }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      disabled={!edit.canEdit || launching !== null}
                      onClick={() => void runNext(run)}
                    >
                      {launching === run.job_id
                        ? t("monthly.launching")
                        : t("monthly.runNextTool", { tool: nextName })}
                    </Button>
                  }
                >
                  {t("monthly.needsNextTool", {
                    n: changes.runs.indexOf(run) + 1,
                    tool:
                      resolveText(run.tool_display_name ?? "", i18n.language) ||
                      run.tools?.[run.tools.length - 1] ||
                      "",
                    next: nextName,
                  })}
                </Alert>
              );
            })}
            {changes.compared.length === 0 && pendingNext.length === 0 && (
              <Alert severity="info">{t("monthly.noFigures")}</Alert>
            )}
            {monthNext && monthNextInput && changes.figures_from && (
              <Alert
                severity="info"
                sx={{ mb: 2 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    disabled={!edit.canEdit || launching !== null}
                    onClick={() => void launch("month", monthNext, monthNextInput)}
                  >
                    {launching === "month"
                      ? t("monthly.launching")
                      : t("monthly.runNextTool", {
                          tool: resolveText(monthNext.display_name, i18n.language) || monthNext.name,
                        })}
                  </Button>
                }
              >
                {t("monthly.figuresAtLevel", {
                  tool:
                    resolveText(changes.figures_from.display_name, i18n.language) ||
                    changes.figures_from.name,
                  next: resolveText(monthNext.display_name, i18n.language) || monthNext.name,
                })}
              </Alert>
            )}
            {changes.compared.length === 1 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t("monthly.oneUpload")}
              </Alert>
            )}
            {comparable && changes.changed_count === 0 && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {t("monthly.noChange")}
              </Alert>
            )}

            {changes.compared.length > 0 && (
              <>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mb: 1.5 }}
                >
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={entity ?? ALL_ENTITIES}
                    onChange={(_e, next: string | null) =>
                      next != null && setEntity(next === ALL_ENTITIES ? null : next)
                    }
                    aria-label={t("monthly.activity")}
                  >
                    <ToggleButton value={ALL_ENTITIES}>{t("monthly.all")}</ToggleButton>
                    {changes.entities.map((name) => (
                      <ToggleButton key={name} value={name}>
                        {name}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={changedOnly && canNarrow ? "changed" : "all"}
                    onChange={(_e, next: string | null) => next && setChangedOnly(next === "changed")}
                    aria-label={t("monthly.colAccount")}
                  >
                    <ToggleButton value="changed" disabled={!canNarrow}>
                      {t("monthly.changedOnly")}
                    </ToggleButton>
                    <ToggleButton value="all">{t("monthly.allAccounts")}</ToggleButton>
                  </ToggleButtonGroup>
                  <TextField
                    size="small"
                    label={t("monthly.searchAccount")}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ minWidth: 240 }}
                  />
                </Stack>

                {rows.length === 0 ? (
                  // nothing changed in the activity shown, or nothing matches the search
                  <EmptyState
                    message={
                      changedOnly && canNarrow && !search.trim()
                        ? t("monthly.noChange")
                        : t("monthly.noMatch")
                    }
                  />
                ) : (
                  <DataTable
                    maxHeight={480}
                    columns={[
                      { key: "account", label: t("monthly.colAccount") },
                      { key: "label", label: t("monthly.colLabel") },
                      { key: "entity", label: t("monthly.colActivity") },
                      ...compared.map(({ run, n }) => ({
                        key: run.job_id,
                        align: "right" as const,
                        label: (
                          <Tooltip
                            title={`${run.file_name ?? t("monthly.noFile")} · ${t("monthly.by", { user: run.created_by })}`}
                          >
                            <span>
                              {t("monthly.uploadN", { n })}
                              <br />
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                className="num"
                              >
                                {formatDateTimeSeconds(run.run_at, i18n.language)}
                              </Typography>
                            </span>
                          </Tooltip>
                        ),
                      })),
                      ...(comparable
                        ? [{ key: "delta", label: t("monthly.colDelta"), align: "right" as const }]
                        : []),
                    ]}
                  >
                    {rows.map((row) => (
                      <TableRow key={`${row.fs_item}-${row.entity}`} hover>
                        <TableCell className="num">{row.fs_item}</TableCell>
                        <TableCell>{row.label}</TableCell>
                        <TableCell>{row.entity}</TableCell>
                        {row.values.map((value, index) => {
                          const moved = cellChanged(row.values, index);
                          return (
                            <TableCell
                              key={compared[index]?.run.job_id ?? index}
                              align="right"
                              className="num"
                              sx={moved ? { bgcolor: changedBg, fontWeight: 600 } : undefined}
                            >
                              {value == null ? (
                                <Typography
                                  component="span"
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontStyle: "italic" }}
                                >
                                  {t("monthly.absent")}
                                </Typography>
                              ) : (
                                fullAmount(Number(value), i18n.language, changes.currency)
                              )}
                            </TableCell>
                          );
                        })}
                        {comparable && (
                          <TableCell
                            align="right"
                            className="num"
                            sx={row.changed ? { fontWeight: 600 } : undefined}
                          >
                            {signedAmount(row.delta, i18n.language, changes.currency)}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </DataTable>
                )}
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/** One upload of the month on the timeline: when, which file, by whom, what it became. */
function UploadCard({ run, n }: { run: MonthRun; n: number }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.25, minWidth: 220, flex: "0 0 auto", cursor: "pointer" }}
      role="button"
      tabIndex={0}
      aria-label={`${t("monthly.uploadN", { n })} — ${t("monthly.openRun")}`}
      onClick={() => navigate(`/runs/${run.job_id}`)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") void navigate(`/runs/${run.job_id}`);
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography variant="subtitle2">{t("monthly.uploadN", { n })}</Typography>
        <StateChip state={run.state} />
      </Stack>
      <Typography variant="body2" className="num">
        {formatDateTimeSeconds(run.run_at, i18n.language)}
      </Typography>
      {run.tool_display_name && (
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          sx={{ display: "block", maxWidth: 200 }}
        >
          {resolveText(run.tool_display_name, i18n.language)}
        </Typography>
      )}
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
        <InsertDriveFileOutlinedIcon fontSize="inherit" color="action" />
        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
          {run.file_name ?? t("monthly.noFile")}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary">
          {t("monthly.by", { user: run.created_by })}
        </Typography>
        {run.harvested === false && (
          <Chip size="small" variant="outlined" label={t("monthly.notHarvested")} />
        )}
        <OpenInNewIcon fontSize="inherit" color="action" sx={{ ml: "auto" }} />
      </Stack>
    </Paper>
  );
}
